import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from "@f2e/ui/components/base"

import { resolveLocalizedText } from "../registry"
import type {
  AgentTemplateFieldDefinition,
  AgentTemplateFormValue,
  AgentTemplateFormItem,
  AgentTemplateFormValues,
  AgentTemplateLocale,
} from "../types"
import { AgentModelItemRenderer } from "./AgentModelItemRenderer"
import { WebSearchItemRenderer } from "./WebSearchItemRenderer"
import { ImageGenerationItemRenderer } from "./ImageGenerationItemRenderer"
import { MemoryItemRenderer } from "./MemoryItemRenderer"
import { TextToSpeechItemRenderer } from "./TextToSpeechItemRenderer"
import { KnowledgeBaseItemRenderer } from "./KnowledgeBaseItemRenderer"
import { RequestFormItemRenderer } from "./RequestFormItemRenderer"
import { HumanHandoffItemRenderer } from "./HumanHandoffItemRenderer"

export interface TemplateItemRendererProps {
  workspaceId: string
  item: AgentTemplateFormItem
  locale: AgentTemplateLocale
  value?: AgentTemplateFormValue
  metadata?: unknown
  values: AgentTemplateFormValues
  error?: string
  disabled?: boolean
  onPendingChange?: (pending: boolean) => void
  onMetadataChange?: (metadata: unknown) => void
  onChange: (value: AgentTemplateFormValue) => void
}

const fieldA11y = (field: AgentTemplateFieldDefinition, error?: string) => {
  const inputId = `template-${field.key}`
  return {
    inputId,
    descriptionId: field.description ? `${inputId}-description` : undefined,
    errorId: error ? `${inputId}-error` : undefined,
  }
}

const FieldMessages = ({
  field,
  locale,
  error,
}: {
  field: AgentTemplateFieldDefinition
  locale: AgentTemplateLocale
  error?: string
}) => {
  const ids = fieldA11y(field, error)
  return (
    <>
      {field.description && (
        <p id={ids.descriptionId} className="text-sm text-muted-foreground">
          {resolveLocalizedText(field.description, locale)}
        </p>
      )}
      {error && (
        <p id={ids.errorId} className="text-sm text-destructive">
          {error}
        </p>
      )}
    </>
  )
}

const TextRenderer = ({
  item,
  locale,
  value,
  error,
  disabled,
  onChange,
}: TemplateItemRendererProps) => {
  if (!("key" in item) || !["text", "agent_name"].includes(item.type))
    return null
  const field = item as AgentTemplateFieldDefinition
  const ids = fieldA11y(field, error)
  return (
    <div className="space-y-2">
      <label htmlFor={ids.inputId} className="text-sm font-medium">
        {resolveLocalizedText(field.label, locale)}
        {field.required ? " *" : ""}
      </label>
      <Input
        id={ids.inputId}
        value={String(value ?? "")}
        disabled={disabled}
        maxLength={"maxLength" in field ? field.maxLength : undefined}
        placeholder={
          "placeholder" in field && field.placeholder
            ? resolveLocalizedText(field.placeholder, locale)
            : undefined
        }
        aria-required={field.required}
        aria-invalid={!!error}
        aria-describedby={[ids.descriptionId, ids.errorId]
          .filter(Boolean)
          .join(" ")}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldMessages field={field} locale={locale} error={error} />
    </div>
  )
}

const TextareaRenderer = (props: TemplateItemRendererProps) => {
  const { item, locale, value, error, disabled, onChange } = props
  if (!("key" in item) || item.type !== "textarea") return null
  const ids = fieldA11y(item, error)
  return (
    <div className="space-y-2">
      <label htmlFor={ids.inputId} className="text-sm font-medium">
        {resolveLocalizedText(item.label, locale)}
        {item.required ? " *" : ""}
      </label>
      <Textarea
        id={ids.inputId}
        value={String(value ?? "")}
        disabled={disabled}
        maxLength={item.maxLength}
        placeholder={
          item.placeholder
            ? resolveLocalizedText(item.placeholder, locale)
            : undefined
        }
        aria-required={item.required}
        aria-invalid={!!error}
        aria-describedby={[ids.descriptionId, ids.errorId]
          .filter(Boolean)
          .join(" ")}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldMessages field={item} locale={locale} error={error} />
    </div>
  )
}

const SelectRenderer = (props: TemplateItemRendererProps) => {
  const { item, locale, value, error, disabled, onChange } = props
  if (!("key" in item) || item.type !== "select") return null
  const ids = fieldA11y(item, error)
  return (
    <div className="space-y-2">
      <label htmlFor={ids.inputId} className="text-sm font-medium">
        {resolveLocalizedText(item.label, locale)}
        {item.required ? " *" : ""}
      </label>
      <Select
        value={String(value ?? "")}
        disabled={disabled}
        onValueChange={(nextValue) => onChange(nextValue)}
      >
        <SelectTrigger
          id={ids.inputId}
          aria-required={item.required}
          aria-invalid={!!error}
          aria-describedby={[ids.descriptionId, ids.errorId]
            .filter(Boolean)
            .join(" ")}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {item.options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {resolveLocalizedText(option.label, locale)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FieldMessages field={item} locale={locale} error={error} />
    </div>
  )
}

const BooleanRenderer = (props: TemplateItemRendererProps) => {
  const { item, locale, value, error, disabled, onChange } = props
  if (!("key" in item) || item.type !== "boolean") return null
  const ids = fieldA11y(item, error)
  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-border p-3">
      <div className="space-y-1">
        <label htmlFor={ids.inputId} className="text-sm font-medium">
          {resolveLocalizedText(item.label, locale)}
        </label>
        <FieldMessages field={item} locale={locale} error={error} />
      </div>
      <Switch
        id={ids.inputId}
        checked={value === true}
        disabled={disabled}
        aria-invalid={!!error}
        aria-describedby={[ids.descriptionId, ids.errorId]
          .filter(Boolean)
          .join(" ")}
        onCheckedChange={(checked) => onChange(checked)}
      />
    </div>
  )
}

const SectionRenderer = ({ item, locale }: TemplateItemRendererProps) => {
  if (item.type !== "section") return null
  return (
    <div className="space-y-1 pt-2">
      <h3 className="text-sm font-semibold">
        {resolveLocalizedText(item.title, locale)}
      </h3>
      {item.description && (
        <p className="text-sm text-muted-foreground">
          {resolveLocalizedText(item.description, locale)}
        </p>
      )}
    </div>
  )
}

const DividerRenderer = ({ item }: TemplateItemRendererProps) =>
  item.type === "divider" ? <hr className="border-border" /> : null

const DescriptionRenderer = ({ item, locale }: TemplateItemRendererProps) =>
  item.type === "description" ? (
    <p className="text-sm text-muted-foreground">
      {resolveLocalizedText(item.text, locale)}
    </p>
  ) : null

export const TemplateItemRenderer = (props: TemplateItemRendererProps) => {
  switch (props.item.type) {
    case "text":
    case "agent_name":
      return <TextRenderer {...props} />
    case "textarea":
      return <TextareaRenderer {...props} />
    case "select":
      return <SelectRenderer {...props} />
    case "boolean":
      return <BooleanRenderer {...props} />
    case "agent_model":
      return <AgentModelItemRenderer {...props} />
    case "section":
      return <SectionRenderer {...props} />
    case "divider":
      return <DividerRenderer {...props} />
    case "description":
      return <DescriptionRenderer {...props} />
    case "tool_web_search":
      return <WebSearchItemRenderer {...props} />
    case "tool_image_generation":
      return <ImageGenerationItemRenderer {...props} />
    case "tool_memory":
      return <MemoryItemRenderer {...props} />
    case "tool_text_to_speech":
      return <TextToSpeechItemRenderer {...props} />
    case "tool_kb":
      return <KnowledgeBaseItemRenderer {...props} />
    case "tool_request_form":
      return <RequestFormItemRenderer {...props} />
    case "tool_human_handoff":
      return <HumanHandoffItemRenderer {...props} />
    default:
      throw new Error("No Agent Template form renderer for item")
  }
}
